/**
 * Safari starts loading `loading="lazy"` images only ~100px before they enter
 * the viewport (Chrome preloads 1250px+ ahead), which leaves visible blank
 * product cards while scrolling grids on iOS.
 *
 * Strategy (first visible slide of each product card only):
 * 1. IntersectionObserver with a 2000px margin upgrades images ahead of scroll.
 * 2. After the load event, the remaining grid images are prefetched in small
 *    batches while the network is idle, so scrolling never outruns loading.
 * 3. WebKit sometimes fails to resume a deferred lazy image when the attribute
 *    changes — if the load doesn't start, it is restarted explicitly.
 */
(() => {
  if (!('IntersectionObserver' in window)) return;

  // WebKit only. Everything below exists solely to compensate for Safari's ~100px lazy-load
  // distance; Chrome already preloads 1250px+ ahead (see the note above), so on Chrome this
  // does no useful work while still forcing a load + layout pass over every grid image. That
  // showed up as the single largest long task on the homepage in GTmetrix (204ms desktop,
  // 159ms mobile), which runs Chrome.
  //
  // navigator.vendor, not a UA regex: every WebKit browser reports 'Apple Computer, Inc.',
  // including Chrome and Firefox on iOS, which share Safari's engine and therefore its
  // lazy-load behaviour. A /safari/ UA test would wrongly exclude those.
  if (navigator.vendor !== 'Apple Computer, Inc.') return;

  const seen = new WeakSet();

  function forceLoad(img) {
    img.loading = 'eager';
    setTimeout(() => {
      if (img.complete || img.currentSrc) return;
      const src = img.getAttribute('src');
      const srcset = img.getAttribute('srcset');
      img.removeAttribute('srcset');
      img.removeAttribute('src');
      if (srcset) img.setAttribute('srcset', srcset);
      if (src) img.setAttribute('src', src);
    }, 0);
  }

  const observer = new IntersectionObserver(
    (entries) => {
      // Spread forced loads across a couple of frames instead of forcing
      // every intersecting image in one synchronous pass (this was the
      // source of the ~750ms long task on grid pages).
      const toLoad = [];
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        toLoad.push(entry.target);
      }
      if (!toLoad.length) return;
      let i = 0;
      (function next() {
        const batch = toLoad.slice(i, i + 4);
        i += 4;
        batch.forEach(forceLoad);
        if (i < toLoad.length) requestAnimationFrame(next);
      })();
    },
    { rootMargin: '600px 0px' }
  );

  function candidates() {
    const imgs = [];
    document.querySelectorAll('.product-grid .card-gallery').forEach((gallery) => {
      const slide = gallery.querySelector('slideshow-slide:not([hidden])');
      const img = (slide || gallery).querySelector('img[loading="lazy"]');
      if (img) imgs.push(img);
    });
    return imgs;
  }

  function scan() {
    candidates().forEach((img) => {
      if (seen.has(img)) return;
      seen.add(img);
      observer.observe(img);
    });
  }

  scan();

  // Grids get re-rendered by filters/pagination — pick up the new images.
  // Scoped to product grid containers only, so unrelated DOM churn
  // elsewhere on the page (cart drawer, modals, consent banners, GTM)
  // doesn't trigger a rescan.
  let scheduled = false;
  const grids = document.querySelectorAll('.product-grid');
  if (grids.length) {
    const mo = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        scan();
      });
    });
    grids.forEach((grid) => mo.observe(grid, { childList: true, subtree: true }));
  }

  // Once the page has loaded, quietly prefetch whatever is left so the whole
  // grid is ready before the user scrolls to it.
  function prefetchRest() {
    const queue = candidates().filter((img) => img.loading === 'lazy' && !img.complete && !img.currentSrc);
    (function next() {
      const batch = queue.splice(0, 3);
      if (!batch.length) return;
      batch.forEach((img) => {
        observer.unobserve(img);
        forceLoad(img);
      });
      setTimeout(next, 300);
    })();
  }

  if (document.readyState === 'complete') {
    setTimeout(prefetchRest, 1500);
  } else {
    window.addEventListener('load', () => setTimeout(prefetchRest, 1500), { once: true });
  }
})();
